(() => {
  if (customElements.get('rr-video-stories')) return;

  class RRVideoStories extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('.rr-vstories__modal');
      this.playerHost = this.querySelector('.rr-vstories__player-host');
      this.progress = this.querySelector('.rr-vstories__progress-value');
      this.titleTarget = this.querySelector('.rr-vstories__modal-title');
      this.avatarTarget = this.querySelector('.rr-vstories__modal-avatar');
      this.playButton = this.querySelector('[data-story-play]');
      this.muteButton = this.querySelector('[data-story-mute]');
      this.templates = [...this.querySelectorAll('.rr-vstories__template')];
      this.triggers = [...this.querySelectorAll('[data-story-open]')];
      this.rail = this.querySelector('.rr-vstories__rail');
      this.currentIndex = 0;
      this.video = null;
      this.keyHandler = event => {
        if (event.key === 'Escape' && this.modal?.classList.contains('show')) this.close();
        if (event.key === 'ArrowLeft' && this.modal?.classList.contains('show')) this.open(this.currentIndex - 1);
        if (event.key === 'ArrowRight' && this.modal?.classList.contains('show')) this.open(this.currentIndex + 1);
      };

      if (!this.modal || !this.playerHost || !this.templates.length) return;

      const Modal = typeof bootstrap !== 'undefined' ? bootstrap.Modal : window.bootstrap?.Modal;
      this.bsModal = Modal?.getOrCreateInstance(this.modal);
      this.addEventListener('click', this.handleClick.bind(this));
      this.modal.addEventListener('hidden.bs.modal', () => this.destroyPlayer());
      document.addEventListener('keydown', this.keyHandler);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.keyHandler);
      this.close();
    }

    handleClick(event) {
      const open = event.target.closest('[data-story-open]');
      if (open && this.contains(open)) {
        this.open(Number(open.dataset.storyOpen || 0));
        return;
      }
      if (event.target.closest('[data-story-prev]')) this.open(this.currentIndex - 1);
      if (event.target.closest('[data-story-next]')) this.open(this.currentIndex + 1);
      if (event.target.closest('[data-story-close]')) this.close();
      if (event.target.closest('[data-rail-prev]')) this.scrollRail(-1);
      if (event.target.closest('[data-rail-next]')) this.scrollRail(1);
      if (event.target.closest('[data-story-play]')) this.togglePlay();
      if (event.target.closest('[data-story-mute]')) this.toggleMute();
    }

    open(index) {
      const count = this.templates.length;
      this.currentIndex = (index + count) % count;
      const template = this.templates[this.currentIndex];
      if (!template) return;

      this.destroyPlayer();
      this.playerHost.appendChild(template.content.cloneNode(true));
      this.video = this.playerHost.querySelector('video');
      this.titleTarget.textContent = template.dataset.title || '';
      this.titleTarget.hidden = !template.dataset.title;

      const avatar = template.dataset.avatar;
      if (avatar) {
        this.avatarTarget.src = avatar;
        this.avatarTarget.alt = '';
        this.avatarTarget.hidden = false;
      } else {
        this.avatarTarget.hidden = true;
        this.avatarTarget.removeAttribute('src');
      }

      if (this.video) {
        this.video.addEventListener('timeupdate', () => this.updateProgress());
        this.video.addEventListener('play', () => this.updateControls());
        this.video.addEventListener('pause', () => this.updateControls());
        this.video.addEventListener('volumechange', () => this.updateControls());
        this.video.addEventListener('ended', () => {
          if (this.dataset.autoAdvance === 'true' && count > 1) this.open(this.currentIndex + 1);
        });
      }

      this.updateArrows();
      this.updateControls();
      if (this.bsModal) this.bsModal.show();
      else this.showFallbackModal();

      const play = this.video?.play();
      if (play?.catch) play.catch(() => this.updateControls());
    }

    destroyPlayer() {
      if (this.video) {
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        this.video.load();
      }
      if (this.playerHost) this.playerHost.replaceChildren();
      this.video = null;
      if (this.progress) this.progress.style.width = '0%';
    }

    showFallbackModal() {
      this.modal.style.display = 'block';
      this.modal.classList.add('show');
      this.modal.removeAttribute('aria-hidden');
      this.modal.setAttribute('aria-modal', 'true');
      document.body.classList.add('modal-open');
      if (!this.backdrop) {
        this.backdrop = document.createElement('div');
        this.backdrop.className = 'modal-backdrop fade show rr-vstories__backdrop';
        this.backdrop.addEventListener('click', () => this.close());
        document.body.appendChild(this.backdrop);
      }
    }

    close() {
      if (this.bsModal) {
        this.bsModal.hide();
        return;
      }
      if (!this.modal) return;
      this.modal.classList.remove('show');
      this.modal.style.display = '';
      this.modal.setAttribute('aria-hidden', 'true');
      this.modal.removeAttribute('aria-modal');
      document.body.classList.remove('modal-open');
      this.backdrop?.remove();
      this.backdrop = null;
      this.destroyPlayer();
    }

    togglePlay() {
      if (!this.video) return;
      if (this.video.paused) this.video.play().catch(() => {});
      else this.video.pause();
    }

    toggleMute() {
      if (!this.video) return;
      this.video.muted = !this.video.muted;
    }

    scrollRail(direction) {
      if (!this.rail) return;
      const item = this.rail.querySelector('.rr-vstories__item');
      const distance = item ? item.getBoundingClientRect().width + 18 : this.rail.clientWidth * 0.8;
      this.rail.scrollBy({ left: distance * direction, behavior: 'smooth' });
    }

    updateProgress() {
      if (!this.video || !this.progress) return;
      const value = this.video.duration ? (this.video.currentTime / this.video.duration) * 100 : 0;
      this.progress.style.width = `${value}%`;
    }

    updateControls() {
      if (!this.video) return;
      this.playButton?.setAttribute('aria-label', this.video.paused ? 'Lire la vidéo' : 'Mettre en pause');
      this.playButton?.classList.toggle('is-paused', this.video.paused);
      this.muteButton?.setAttribute('aria-label', this.video.muted ? 'Activer le son' : 'Couper le son');
      this.muteButton?.classList.toggle('is-muted', this.video.muted);
    }

    updateArrows() {
      const hidden = this.templates.length < 2;
      this.querySelectorAll('[data-story-prev], [data-story-next]').forEach(button => { button.hidden = hidden; });
    }
  }

  customElements.define('rr-video-stories', RRVideoStories);
})();
